import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

function ComparePlansWidget(props) {

    const { t } = useTranslation();

    const [itemDesc, setItemDesc] = useState(null);
    const [modalTopPosition, setModalTopPosition] = useState(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const headerElement = document.querySelector('.header-top-bg');

        if (!headerElement) {
            setHeaderHeight(0);
            return;
        }

        setHeaderHeight(headerElement.clientHeight);

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === headerElement) {
                    setHeaderHeight(entry.contentRect.height);
                }
            }
        });

        observer.observe(headerElement);

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const handleResize = () => { setItemDesc(null); };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getValue = (plan, type, name, i) => {
        return (
            <div
                className={
                    "compare-plans-element text-center text-medium text-md-small px-3" +
                    (plan?.icons
                        ? " d-flex flex-wrap justify-content-around justify-content-sm-center align-items-center"
                        : "")
                }
            >
                {plan?.text && (
                    <p data-test-id={`pcs-item-t-${type}-${name}-${i}`}>
                        {plan.text}
                    </p>
                )}

                {plan?.icons &&
                    plan.icons.map((value, index) => (
                        <span
                            data-test-id={`pcs-item-s-${type}-${name}-${i}`}
                            key={index}
                            className={
                                'cp_symbol d-block mr-0 mr-sm-1 mr-md-2 ' + value
                            }
                        />
                    ))}
            </div>
        );
    };

    const showFaq = (e, description) => {
        const iconRect = e.currentTarget.getBoundingClientRect();
        // контейнер, относительно которого позиционируется модалка
        const container = e.currentTarget.closest('.compare-plans');
        const containerRect = container.getBoundingClientRect();

        // TOP: (Y иконки - Y контейнера) + высота иконки
        // десктоп
        let top = iconRect.top - containerRect.top;

        // мобилка
        if (window.innerWidth < 575) {
            top += iconRect.height + 8;
        }

        setModalTopPosition(top);
        setItemDesc(description);
    };

    return (
        <div className="compare-plans container-xl px-0 mx-auto mt-4 mb-5">
            <div
                style={{ top: headerHeight + 'px' }}
                className="compare-plans-head d-flex justify-content-between align-items-center flex-wrap mb-0 mb-lg-4 px-0 px-lg-3 py-0 py-lg-2"
            >
                <h2 className="text-color-changeable text-md-center pr-3 pl-3 pl-lg-0 mb-2 mb-sm-3 mb-lg-0">
                    {t('Compare plans')}
                </h2>

                {props.groups?.map((item, index) => (
                    <h3
                        key={`p_${index}`}
                        className="text-center px-3 py-1 py-lg-0"
                    >
                        {item}
                    </h3>
                ))}
            </div>

            <div className="compare-plans-body py-4 px-0 px-lg-3">
                {props.plans_details?.map((items, index) => {
                    const rowClass =
                        index % 2 === 0 ? ' bg-gray' : ' bg-red';

                    return (
                        <div key={index} className="mb-32">
                            <p
                                data-test-id={`pcs-${items.section}-${index}`}
                                className="compare-plans-section text-md-center text-bold text-color-changeable mb-12 pr-3 pl-md-16 pl-100"
                            >
                                {items.section}
                            </p>

                            {items.values?.map((item, item_index) => (
                                <div
                                    key={`cat_${item_index}`}
                                    className={
                                        'compare-plans-item d-flex justify-content-between align-items-center flex-wrap mb-1 py-2 py-lg-1' +
                                        rowClass
                                    }
                                >
                                    <div className="compare-plans-element text-md-center text-medium text-md-small text-md text-bold pr-3 pl-md-16 pl-100 mb-3 mb-lg-0">
                                        {item.description?.length > 0 && (
                                            <span
                                                className="compare-plans-info d-inline-flex justify-content-center align-items-center text-bold"
                                                onMouseEnter={(e) => showFaq(e, item.description)}
                                                onClick={(e) => showFaq(e, item.description)}
                                                onFocus={(e) => showFaq(e, item.description)}
                                                onMouseLeave={() => setItemDesc(null)}
                                                onBlur={() => setItemDesc(null)}
                                            >
                                                ?
                                            </span>
                                        )}

                                        <span
                                            data-test-id={`pcs-item-${item.title}-${item_index}`}
                                        >
                                            {item.title}
                                        </span>
                                    </div>

                                    {getValue(
                                        item?.contents?.demo,
                                        'demo',
                                        item.title,
                                        item_index
                                    )}
                                    {getValue(
                                        item?.contents?.basic,
                                        'basic',
                                        item.title,
                                        item_index
                                    )}
                                    {getValue(
                                        item?.contents?.super,
                                        'super',
                                        item.title,
                                        item_index
                                    )}
                                    {getValue(
                                        item?.contents?.ultra,
                                        'ultra',
                                        item.title,
                                        item_index
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>

            <div
                style={{ top: `${modalTopPosition ?? 0}px` }}
                className={
                    'compare-plans-information text-justify p-3 p-lg-4' +
                    (itemDesc ? ' d-block' : ' d-none')
                }
            >
                <span
                    onClick={() => setItemDesc(null)}
                    className="d-flex d-sm-none text-white text-bold justify-content-center align-items-center mx-auto pb-2 mb-3"
                >
                    x
                </span>

                <div
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(itemDesc || '')
                    }}
                />
            </div>
        </div>
    );
}


ComparePlansWidget.propTypes = {
    title: PropTypes.string,
    descriptions: PropTypes.string,
    plans_details: PropTypes.array,
    additional_text: PropTypes.string,
    button_text: PropTypes.string,
    path: PropTypes.string,
    groups: PropTypes.array,
    img: PropTypes.string,
    items: PropTypes.array,
    position: PropTypes.number,
    widget: PropTypes.string,
    className: PropTypes.string,
};

export default ComparePlansWidget;
